import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { getAnomalies, type AnomalyEvent } from "@/services/securityRecords";
import AdminDataTable from "@/components/admin/AdminDataTable";
import AdminUserCell from "@/components/admin/AdminUserCell";
import { AdminSelectFilter, AdminTableToolbar } from "@/components/admin/AdminTableToolbar";

const behaviorTypeClass = (type: AnomalyEvent["behaviorType"]) => {
  switch (type) {
    case "frequency":
      return "bg-orange-50 text-orange-700 border border-orange-200";
    case "duplicate":
      return "bg-rose-50 text-rose-700 border border-rose-200";
    default:
      return "bg-gray-50 text-gray-600 border border-gray-100";
  }
};

const behaviorTypeLabel = (type: AnomalyEvent["behaviorType"], t: (k: string) => string) => {
  switch (type) {
    case "frequency":
      return t("anomaly.behaviorType.frequency");
    case "duplicate":
      return t("anomaly.behaviorType.duplicate");
    default:
      return type;
  }
};

export default function AnomalyBehaviorList() {
  const { t } = useTranslation();
  const [events, setEvents] = useState<AnomalyEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [hoursFilter, setHoursFilter] = useState("24");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAnomalies({ hours: Number(hoursFilter), page, pageSize });
      setEvents(res.data);
      setTotal(res.total);
    } catch {
      setEvents([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [hoursFilter, page, pageSize]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const toggleRow = (id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  return (
    <div className="rounded-xl border border-gray-100 bg-white p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-800">
          {t("anomaly.behaviorListTitle")}
        </h3>
        <span className="text-xs text-gray-400">{t("anomaly.behaviorListHint", { count: total })}</span>
      </div>
      <AdminTableToolbar>
        <AdminSelectFilter
          label={t("anomaly.filter.timeRange")}
          value={hoursFilter}
          onChange={(value) => {
            setHoursFilter(value);
            setPage(1);
          }}
          options={[
            { value: "1", label: t("anomaly.filter.last1Hour") },
            { value: "4", label: t("anomaly.filter.last4Hours") },
            { value: "24", label: t("anomaly.filter.last24Hours") },
            { value: "168", label: t("anomaly.filter.last7Days") },
          ]}
        />
      </AdminTableToolbar>
      <AdminDataTable
        headers={[
          t("anomaly.col.behaviorType"),
          t("anomaly.col.user"),
          t("anomaly.col.detail"),
          t("anomaly.col.questionText"),
          t("anomaly.col.time"),
        ]}
        colSpan={5}
        loading={loading}
        empty={events.length === 0}
        emptyText={t("anomaly.behaviorListTitle")}
        emptyHint={t("users.table.noResultsHint")}
        total={total}
        page={page}
        pageSize={pageSize}
        onPageChange={(nextPage, nextPageSize) => {
          setPage(nextPage);
          setPageSize(nextPageSize);
        }}
      >
        {events.map((e) => {
            const isExpanded = expandedRows.has(e.id);
            const hasExpandContent =
              e.questionText.length > 80 ||
              e.detail.length > 60;
            return (
              <tr key={e.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                  <td className="px-3 py-2.5 align-top">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${behaviorTypeClass(e.behaviorType)}`}
                    >
                      {behaviorTypeLabel(e.behaviorType, t)}
                    </span>
                  </td>
                  <td className="px-4 py-4 align-top">
                    <AdminUserCell name={e.userName} userId={e.userId} />
                  </td>
                  <td className="px-3 py-2.5 text-xs text-gray-600 leading-relaxed align-top">
                    <div className="flex items-start gap-1">
                      <div className={isExpanded ? "" : "line-clamp-2"}>
                        {e.behaviorType === "frequency"
                          ? t("anomaly.detail.frequency", { count: e.requestCount, minutes: e.windowMinutes })
                          : t("anomaly.detail.duplicate", { count: e.requestCount, minutes: e.windowMinutes })}
                      </div>
                      {hasExpandContent && (
                        <button
                          onClick={() => toggleRow(e.id)}
                          className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-700 cursor-pointer transition-colors shrink-0 mt-0.5"
                          title={isExpanded ? t("common.showLess") : t("common.showMore")}
                        >
                          <i
                            className={`text-sm transition-transform ${isExpanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}`}
                          ></i>
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-gray-700 text-xs leading-relaxed align-top">
                    <div className={isExpanded ? "" : "line-clamp-2"}>
                      {e.behaviorType === "duplicate" ? e.questionText : ""}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-xs text-gray-500 whitespace-nowrap">
                    {e.timestamp}
                  </td>
              </tr>
            );
          })}
      </AdminDataTable>
    </div>
  );
}
